import { Component } from '@theme/component';

/**
 * Parses a Brazilian-formatted date string into epoch ms (visitor local time).
 * Accepts "DD/MM/AAAA", "DD/MM/AAAA HH:MM" or "DD/MM/AAAA HH:MM:SS".
 *
 * Exported so other countdown surfaces (assets/countdown-inline.js) read the
 * merchant's date the exact same way instead of shipping a second parser.
 *
 * @param {string} value
 * @returns {number} epoch ms, or 0 when invalid.
 */
export function parseBrDate(value) {
  const raw = value.trim();
  if (!raw) return 0;

  const [datePart = '', timePart = ''] = raw.split(/[ T]+/);
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);

  if (!day || !month || !year) return 0;

  const date = new Date(year, month - 1, day, hours, minutes, seconds);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

/**
 * A full-width promotional banner with a live countdown timer and a dismiss button.
 *
 * Timer modes (via `data-mode`):
 *  - `fixed`: counts down to the ISO datetime in `data-end` (store local time).
 *  - `evergreen`: counts down `data-minutes` from the visitor's first view, persisted per browser.
 *
 * Dismissal is remembered in localStorage keyed by the current deadline, so a new
 * campaign (new end date / duration) surfaces the banner again.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @extends {Component<Refs>}
 */
export class CountdownBanner extends Component {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number} Deadline as epoch milliseconds. */
  #deadline = 0;

  connectedCallback() {
    super.connectedCallback();

    // The inline pre-paint gate hides expired fixed banners before first paint.
    // Clear its initial flag here so the JS is the single source of truth for
    // visibility from now on (and the banner still shows if the gate was skipped).
    this.removeAttribute('data-cb-init');

    if (this.#isDismissed()) {
      this.hidden = true;
      return;
    }

    this.#deadline = this.#resolveDeadline();

    // Already expired on load with hide-on-complete: stay hidden, don't paint/tick.
    if (
      this.dataset.onComplete === 'hide' &&
      this.#deadline &&
      this.#deadline - Date.now() <= 0
    ) {
      this.hidden = true;
      return;
    }

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    clearInterval(this.#interval);
  }

  /**
   * Dismiss the banner and remember the choice for this deadline.
   */
  close() {
    clearInterval(this.#interval);
    this.hidden = true;
    try {
      localStorage.setItem(this.#storageKey, String(this.#deadline));
    } catch {
      // Private mode / storage disabled — dismissal just won't persist.
    }
    this.dispatchEvent(new CustomEvent('countdown-banner:closed', { bubbles: true }));
  }

  get #storageKey() {
    return `countdown-banner:${this.id || this.dataset.sectionId || 'default'}`;
  }

  #isDismissed() {
    try {
      return localStorage.getItem(this.#storageKey) === String(this.#resolveDeadline());
    } catch {
      return false;
    }
  }

  /**
   * @returns {number} epoch milliseconds for the countdown target.
   */
  #resolveDeadline() {
    if (this.#deadline) return this.#deadline;

    // The End date is a hard campaign stop in both modes.
    const endDate = this.#parseBrDate(this.dataset.end ?? '');

    if (this.dataset.mode === 'evergreen') {
      const minutes = parseInt(this.dataset.minutes ?? '0', 10) || 0;
      const key = `${this.#storageKey}:start`;
      let start = 0;
      try {
        start = parseInt(localStorage.getItem(key) ?? '0', 10);
        if (!start) {
          start = Date.now();
          localStorage.setItem(key, String(start));
        }
      } catch {
        start = Date.now();
      }
      const evergreenDeadline = start + minutes * 60_000;
      // Never let the per-visit timer run past the campaign's End date.
      return endDate ? Math.min(evergreenDeadline, endDate) : evergreenDeadline;
    }

    // Fixed mode — expects Brazilian format "DD/MM/AAAA HH:MM:SS" (time optional).
    return endDate;
  }

  /**
   * @param {string} value
   * @returns {number} epoch ms, or 0 when invalid.
   */
  #parseBrDate(value) {
    return parseBrDate(value);
  }

  #tick() {
    if (!this.#deadline) {
      // No valid deadline configured — nothing to count.
      clearInterval(this.#interval);
      return;
    }

    const diff = Math.max(0, this.#deadline - Date.now());

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86_400);
    const hours = Math.floor((totalSeconds % 86_400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // When the days unit isn't shown, roll days into hours so the timer stays accurate.
    const showDays = Boolean(this.refs.days);
    this.#render(this.refs.days, days);
    this.#render(this.refs.hours, showDays ? hours : hours + days * 24);
    this.#render(this.refs.minutes, minutes);
    this.#render(this.refs.seconds, seconds);

    if (diff <= 0) {
      clearInterval(this.#interval);
      if (this.dataset.onComplete === 'hide') this.hidden = true;
    }
  }

  /**
   * @param {HTMLElement|undefined} el
   * @param {number} value
   */
  #render(el, value) {
    if (el) el.textContent = String(value).padStart(2, '0');
  }
}

if (!customElements.get('countdown-banner-component')) {
  customElements.define('countdown-banner-component', CountdownBanner);
}
